import { gql } from '@apollo/client'
import React, { useMemo, useState } from 'react'
import * as yup from "yup"
import { Row, Col, Alert, Button, UncontrolledPopover, PopoverBody, PopoverHeader, UncontrolledTooltip } from 'reactstrap'
import { useApolloClient, useQuery } from '@apollo/client'
import toTitleCase from '../../Functions/toTitleCase'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { PeopleSchema } from '../IncidentModals/IncidentModalsSchema'
import SearchPlaces from '../../Material/SearchPlaces'
import AddRepresentativeModal from '../IncidentModals/AddRepresentativeModal'
import { SEAT_ARRANGEMENT } from '../../Constants'
import { RepresentativeSelect, formatRepresentativeOption } from '../IncidentModals/RepresentativeSelect'

const UpdateIncidentPartyPeople = gql`
    mutation updateIncidentPartyPeopleMutation($input: UpdateIncidentPartyPeopleMutationInput!){
    updateIncidentPartyPeople(input:$input){
        ok
        errors{
            field
            messages
        }
    }
} `


const IncidentPartyPeopleInjuriesChoices = gql`
    query IncidentPartyPeopleInjuriesChoices {
        incidentPartyPeopleInjuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
        enumValues {
            name
            description
        }
    }
}`

const PersonInfo = ({ person, refetchQuery, isDefault, hasRental, colSize = 3, passengerPosition, isReadOnly = false }) => {
    const apolloClient = useApolloClient() // Get the Apollo Client instance
    const [editField, setEditField] = useState("")
    const [inputValues, setInputValues] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [error, setError] = useState(null)
    const [openAddRepresentativeModal, setOpenAddRepresentativeModal] = useState(false) // State for AddRepresentativeModal
    const [newlyCreatedRepresentative, setNewlyCreatedRepresentative] = useState(null)
    const { data: injuriesChoices } = useQuery(IncidentPartyPeopleInjuriesChoices)


    const editFieldHandler = (fieldName, fieldValue = "") => {
        if (!canEditField(fieldName)) return ""
        if (editField && editField !== fieldName) {
            return "Please complete the edit on the current field before editing another field."
        }

        setEditField(fieldName)
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
        return ""
    }

    const saveFieldHandler = (partyPeopleId, fieldName, fieldValue) => {
        if (!canEditField(fieldName)) return ""
        if (fieldErrors[fieldName]) return
        if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
            cancelEditHandler()
            return
        }
        updatePartyPeopleHandler(partyPeopleId, fieldName, fieldValue)
    }


    const updatePartyPeopleHandler = async (partyPeopleId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }

            const isValidValue = typeof trimmedValue === "boolean" || Boolean(trimmedValue)
            if (!partyPeopleId || !name || !isValidValue) return

            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")

            const input = {
                partyPeopleId: partyPeopleId,
                [name]: trimmedValue
            }

            try {
                const result = await apolloClient.mutate({
                    mutation: UpdateIncidentPartyPeople,
                    variables: { input },
                })

                const { ok, errors } = result?.data?.updateIncidentPartyPeople || {}

                if (ok) {
                    setEditField("")
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetchQuery()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField("")
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : rawValue
        }))
    }

    const cancelEditHandler = () => {
        if (!editField) return
        setEditField("")
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }

    const handleAddRepresentative = async (newRepresentative) => {
        if (!newRepresentative?.id) {
            setOpenAddRepresentativeModal(false)
            return
        }

        setNewlyCreatedRepresentative({
            ...formatRepresentativeOption(newRepresentative),
        })
        setEditField("representativeId")
        setInputValues(prev => ({ ...prev, representativeId: newRepresentative.id }))
        setOriginalValues(prev => ({ ...prev, representativeId: person?.respresentative?.id || "" }))
        setFieldErrors(prev => ({ ...prev, representativeId: "" }))
        setError(null)
        setOpenAddRepresentativeModal(false)
    }

    const isDefaultVehicleDriver = Boolean(isDefault && hasRental && person.role === "VEHICLE_DRIVER")
    const canEdit = !isReadOnly && !person?.isArchived
    const editableFieldsForDefaultVehicleDriver = new Set(["representativeId", "injuries", "isBusinessEntity", "statement"])
    const canEditField = (fieldName) => {
        if (!canEdit) return false
        if (!isDefaultVehicleDriver) return true
        return editableFieldsForDefaultVehicleDriver.has(fieldName)
    }
    const currentRepresentativeOption = useMemo(() => {
        if (!person?.respresentative?.id) return null
        return formatRepresentativeOption({
            id: person.respresentative.id,
            name: person.respresentative.name,
            businessName: person.respresentative.businessName,
            phone: person.respresentative.phone,
            email: person.respresentative.email,
            address: person.respresentative.address,
            role: person.respresentative.role,
        })
    }, [person?.respresentative])
    const selectedRepresentativeId = inputValues?.representativeId ?? person?.respresentative?.id
    const representativeOptions = useMemo(() => {
        return [newlyCreatedRepresentative, currentRepresentativeOption].filter(Boolean)
    }, [currentRepresentativeOption, newlyCreatedRepresentative])
    const selectedRepresentative = representativeOptions.find((rep) => rep.value === selectedRepresentativeId) || currentRepresentativeOption
    const representativeInfoTarget = `representative-info-${person.pk}`
    const addRepresentativeTarget = `add-representative-${person.pk}`

    return (
        <Row key={person?.id}>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            {<Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Role"
                    fieldName={"role"}
                    inputType="text"
                    showEdit={false}
                    isEditing={editField === "role"}
                    isLoading={!!fieldLoading?.role}
                    inputValue={inputValues?.["role"]}
                    defaultValue={toTitleCase(person?.role)}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "role")}
                />
            </Col>}
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Name"
                    fieldName={"name"}
                    inputType="text"
                    showEdit={canEditField("name")}
                    isEditing={editField === "name"}
                    isLoading={!!fieldLoading?.name}
                    inputValue={inputValues?.["name"]}
                    defaultValue={person?.name}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "name")}
                />
            </Col>
            {person?.role === "VEHICLE_PASSENGER" && (
                <Col xs={colSize} className="mb-2">
                    <CustomYupInputForm
                        label="Seat Position"
                        fieldName={"passengerPosition"}
                        inputType="select"
                        options={SEAT_ARRANGEMENT}
                        showEdit={canEditField("passengerPosition")}
                        isEditing={editField === "passengerPosition"}
                        isLoading={!!fieldLoading?.passengerPosition}
                        inputValue={inputValues?.["passengerPosition"]}
                        defaultValue={passengerPosition || ""}
                        onEdit={editFieldHandler}
                        onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                        onChange={updateInputHandler}
                        onCancel={cancelEditHandler}
                        yupSchema={yup.string().nullable()}
                    />
                </Col>
            )}
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Email"
                    fieldName={"email"}
                    inputType="email"
                    showEdit={canEditField("email")}
                    isEditing={editField === "email"}
                    isLoading={!!fieldLoading?.email}
                    inputValue={inputValues?.["email"]}
                    defaultValue={person?.email}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "email")}
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Phone"
                    fieldName={"phoneNumber"}
                    inputType="text"
                    showEdit={canEditField("phoneNumber")}
                    isEditing={editField === "phoneNumber"}
                    isLoading={!!fieldLoading?.phoneNumber}
                    inputValue={inputValues?.["phoneNumber"]}
                    defaultValue={person?.phoneNumber}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "phoneNumber")}
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="License"
                    fieldName={"driverLicense"}
                    inputType="text"
                    showEdit={canEditField("driverLicense")}
                    isEditing={editField === "driverLicense"}
                    isLoading={!!fieldLoading?.driverLicense}
                    inputValue={inputValues?.["driverLicense"]}
                    defaultValue={person?.driverLicense}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "driverLicense")}
                />
            </Col>
            <Col xs={colSize}>
                <CustomYupInputForm
                    label="Address"
                    fieldName={"address"}
                    inputType="text"
                    showEdit={canEditField("address")}
                    isEditing={editField === "address"}
                    isLoading={!!fieldLoading?.address}
                    inputValue={inputValues?.["address"]}
                    defaultValue={person?.address}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "address")}
                    renderCustomInput={() =>
                        <SearchPlaces
                            onSaveAction={(place) => { setInputValues({ ...inputValues, address: place?.name || "" }) }}
                            initialValue={person?.address || ""}
                            searchType="address"
                        />
                    }
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <div className="d-flex align-items-end gap-2">
                    <span id={representativeInfoTarget} className="flex-grow-1">
                        <CustomYupInputForm
                            label="Representative"
                            fieldName={"representativeId"}
                            inputType="select"
                            showEdit={canEditField("representativeId")}
                            isEditing={editField === "representativeId"}
                            isLoading={!!fieldLoading?.representativeId}
                            inputValue={inputValues?.["representativeId"]}
                            options={representativeOptions}
                            defaultValue={person?.respresentative?.id || ""}
                            onEdit={editFieldHandler}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            renderCustomInput={() => (
                                <RepresentativeSelect
                                    value={inputValues?.["representativeId"] ?? person?.respresentative?.id ?? null}
                                    onChange={(optionValue) => setInputValues(prev => ({ ...prev, representativeId: optionValue }))}
                                    currentOption={currentRepresentativeOption}
                                    extraOptions={newlyCreatedRepresentative ? [newlyCreatedRepresentative] : []}
                                />
                            )}
                        />
                    </span>
                    {editField === "representativeId" && (
                        <span>
                            <Button className="add-rep-btn" onClick={() => setOpenAddRepresentativeModal(true)} id={addRepresentativeTarget}>
                                <i className="fa fa-plus" />
                            </Button>
                            <UncontrolledTooltip target={addRepresentativeTarget}>Add Representative</UncontrolledTooltip>
                        </span>
                    )}
                </div>
                {editField !== "representativeId" && (
                    <UncontrolledPopover trigger="hover" placement="right" target={representativeInfoTarget}>
                        <PopoverHeader>Representative Info</PopoverHeader>
                        <PopoverBody>
                            <b>Name:</b> <small>{selectedRepresentative?.name || "--"} </small><br />
                            <b>Business Name:</b> <small>{selectedRepresentative?.businessName || "--"} </small><br />
                            <b>Phone:</b> <small>{selectedRepresentative?.phone || "--"} </small><br />
                            <b>Email:</b> <small>{selectedRepresentative?.email || "--"} </small><br />
                            <b>Address:</b> <small>{selectedRepresentative?.address || "--"} </small><br />
                            <b>Role:</b> <small>{selectedRepresentative?.role || "--"} </small><br />
                        </PopoverBody>
                    </UncontrolledPopover>
                )}
                {openAddRepresentativeModal && (
                    <AddRepresentativeModal
                        isOpen={openAddRepresentativeModal}
                        toggle={() => setOpenAddRepresentativeModal(false)}
                        onCreate={handleAddRepresentative}
                    />
                )}
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Injuries"
                    fieldName={"injuries"}
                    inputType="select"
                    showEdit={canEditField("injuries")}
                    options={injuriesChoices?.incidentPartyPeopleInjuriesChoices?.enumValues?.map(choice => ({ label: choice.name, value: choice.name }))}
                    isEditing={editField === "injuries"}
                    isLoading={!!fieldLoading?.injuries}
                    inputValue={inputValues?.["injuries"]}
                    defaultValue={person?.injuries}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "injuries")}
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Is Business Entity"
                    fieldName={"isBusinessEntity"}
                    inputType="select"
                    showEdit={canEditField("isBusinessEntity")}
                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                    isEditing={editField === "isBusinessEntity"}
                    isLoading={!!fieldLoading?.isBusinessEntity}
                    inputValue={inputValues?.["isBusinessEntity"]}
                    defaultValue={person?.isBusinessEntity}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "isBusinessEntity")}
                />
            </Col>
            <Col xs={12} className="mb-2">
                <CustomYupInputForm
                    label="Statement"
                    fieldName={"statement"}
                    inputType="text"
                    showEdit={canEditField("statement")}
                    isEditing={editField === "statement"}
                    isLoading={!!fieldLoading?.statement}
                    inputValue={inputValues?.["statement"]}
                    defaultValue={person?.statement}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "statement")}
                />
            </Col>
        </Row>
    )
}

export default PersonInfo
